import type { DocumentPageSchema } from '@avisoma/shared';
import StatusIndicator from '../../components/StatusIndicator';
import { SupervisorFindingBadge } from '../../components/SupervisorFindingBadge';
import { formatRelativeTime } from '../../lib/dateFormat';
import { pageSupervisorDetail, presentSupervisorRun } from '../../lib/supervisorRunPresentation';

// Supervisor — a project-level stage: the run stamp belongs to the page's project,
// the findings listed under it are the ones asserted on THIS page. Hidden until a run
// stamp exists, so it stays out of the way while the supervisor is off.
export function SupervisorRow({ page }: { page: DocumentPageSchema }) {
  const run = page.supervisorRun;
  if (!run) return null;
  const supervisor = presentSupervisorRun(run);
  const findings = page.supervisorFindings ?? [];
  const timing =
    [supervisor.timings, run?.finishedAt ? formatRelativeTime(run.finishedAt) : null]
      .filter((s): s is string => s !== null)
      .join(' · ') || null;

  return (
    <div>
      <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400 flex items-center gap-2">
        <StatusIndicator status={supervisor.indicator} title="Supervisor" />
        <span className="flex items-baseline gap-1.5 min-w-0 flex-wrap">
          <span>Supervisor</span>
          {timing && (
            <span className="text-xs font-normal tabular-nums text-zinc-500 dark:text-zinc-400 shrink-0">{timing}</span>
          )}
        </span>
      </dt>
      <dd>
        <p
          className={
            supervisor.indicator === 'error'
              ? 'mt-1 text-sm text-red-600 dark:text-red-400'
              : 'mt-1 text-sm text-gray-700 dark:text-zinc-300'
          }
        >
          {pageSupervisorDetail(run, findings.length)}
        </p>
        {findings.length > 0 && (
          <ul className="mt-2 space-y-1">
            {findings.map((f) => (
              <li key={f.id} className="flex flex-wrap items-baseline gap-2">
                <SupervisorFindingBadge finding={f} />
                <span className="text-xs text-gray-500 dark:text-zinc-400">{f.reason}</span>
              </li>
            ))}
          </ul>
        )}
      </dd>
    </div>
  );
}
